import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Navigate } from '@ngxs/router-plugin';
import { Action, NgxsAfterBootstrap, State, StateContext } from '@ngxs/store';
import { createRequestAction, RequestState } from 'ngxs-requests-plugin';
import { StateResetAll } from 'ngxs-reset-plugin';

import { environment } from '../../../../environments/environment';
import { IUser } from '../../interfaces/auth.interface';
import { LocalStorageService } from '../../services/local-storage.service';
import {
  ClearToken,
  GetTokenFromLocalStorage,
  GetUserInfo,
  GetUserInfoFail,
  GetUserInfoSuccess,
  LogOut,
  LogOutFail,
  LogOutSuccess,
  ResendVerificationEmail,
  ResendVerificationEmailFail,
  ResendVerificationEmailSuccess,
  ResetPassword,
  ResetPasswordConfirm,
  ResetPasswordConfirmFail,
  ResetPasswordConfirmSuccess,
  ResetPasswordFail,
  ResetPasswordSuccess,
  SetToken,
  SetUser,
  SignIn,
  SignInFail,
  SignInSuccess,
  SignUp,
  SignUpFail,
  SignUpSuccess,
  UpdatePassword,
  UpdatePasswordFail,
  UpdatePasswordSuccess,
  UpdateUser,
  UpdateUserFail,
  UpdateUserSuccess,
  AcceptTerms,
  AcceptTermsFail,
  AcceptTermsSuccess,
  VerifyEmail,
  VerifyEmailFail,
  VerifyEmailSuccess,
} from './auth.actions';

export interface AuthStateModel {
  authorization: string | null;
  currentUser: IUser | null;
}

@RequestState('getUserInfo')
@Injectable()
export class GetUserInfoRequestState {
}

@RequestState('signIn')
@Injectable()
export class SignInRequestState {
}

@RequestState('signUp')
@Injectable()
export class SignUpRequestState {
}

@RequestState('resendVerificationEmail')
@Injectable()
export class ResendVerificationEmailRequestState {
}

@RequestState('verifyEmail')
@Injectable()
export class VerifyEmailRequestState {
}

@RequestState('resetPassword')
@Injectable()
export class ResetPasswordRequestState {
}

@RequestState('resetPasswordConfirm')
@Injectable()
export class ResetPasswordConfirmRequestState {
}

@RequestState('logOut')
@Injectable()
export class LogOutRequestState {
}

@RequestState('updatePassword')
@Injectable()
export class UpdatePasswordRequestState {
}

@RequestState('updateUser')
@Injectable()
export class UpdateUserRequestState {
}

@RequestState('acceptTerms')
@Injectable()
export class AcceptTermsRequestState {
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    authorization: null,
    currentUser: null,
  },
})
@Injectable()
export class AuthState implements NgxsAfterBootstrap {
  constructor(
    private localStorageService: LocalStorageService,
    private http: HttpClient,
  ) {
  }

  ngxsAfterBootstrap({ dispatch }: StateContext<AuthStateModel>) {
    dispatch([new GetTokenFromLocalStorage()]);
  }

  @Action(SetToken)
  setToken(
    { patchState }: StateContext<AuthStateModel>,
    { payload: authorization }: SetToken
  ) {
    if (authorization) {
      patchState({ authorization });
    }
  }

  @Action(SetUser)
  setUser(
    { patchState }: StateContext<AuthStateModel>,
    { payload: user }: SetUser
  ) {
    patchState({currentUser: user});
  }

  @Action(GetTokenFromLocalStorage)
  getTokenFromLocalStorage(
    { dispatch }: StateContext<AuthStateModel>
  ) {
    const token = this.localStorageService.getItem('sequenza_token');
    if (token) {
      dispatch([new SetToken(token), new GetUserInfo()]);
    }
  }

  @Action([ClearToken, LogOutSuccess])
  clearToken(
    { dispatch, patchState }: StateContext<AuthStateModel>
  ) {
    this.localStorageService.removeItem('sequenza_token');
    patchState({ authorization: null, currentUser: null });

    return dispatch([
      // new StateResetAll(),
      new Navigate(['/auth'])
    ]);
  }

  @Action(GetUserInfo)
  getUserInfo(
    { getState, dispatch }: StateContext<AuthStateModel>
  ) {
    // Check cache first
    const currentUser = getState().currentUser;
    if (currentUser) {
      return undefined;
    }

    const request = this.http.get(environment.api + 'user/get_info');

    return dispatch(
      createRequestAction({
        request,
        state: GetUserInfoRequestState,
        successAction: GetUserInfoSuccess,
        failAction: GetUserInfoFail,
      })
    );
  }

  @Action(GetUserInfoSuccess)
  getUserInfoSuccess(
    { patchState }: StateContext<AuthStateModel>,
    { payload: user }: GetUserInfoSuccess
  ) {
    patchState({currentUser: user});
  }

  @Action(SignIn)
  signIn(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: {username, password} }: SignIn
  ) {
    const request = this.http.post(environment.api + 'user/login', {username, password});

    return dispatch(
      createRequestAction({
        request,
        state: SignInRequestState,
        successAction: SignInSuccess,
        failAction: SignInFail,
        metadata: username
      }),
    );
  }

  @Action(SignInSuccess)
  signInSuccess(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: user }: SignInSuccess,
  ) {
    this.localStorageService.setToken(user.authorization);
    dispatch([new SetToken(user.authorization), new SetUser(user), new Navigate(['/', 'home'])]);
  }

  @Action(SignInFail)
  signInFail(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: response, metadata: username }: SignInFail
  ) {
    if (response?.error?.error === "Email not verified") {
      dispatch(new Navigate(['/', 'auth', 'verification'], {username}));
    }
  }

  @Action(SignUp)
  signUp(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: input }: SignUp
  ) {
    const request = this.http.post(environment.api + 'user/register', input);

    return dispatch(
      createRequestAction({
        request,
        state: SignUpRequestState,
        successAction: SignUpSuccess,
        failAction: SignUpFail,
        metadata: input
      }),
    );
  }

  @Action(SignUpSuccess)
  signUpSuccess(
    { patchState }: StateContext<AuthStateModel>,
    { payload: response, metadata: currentUser }: SignUpSuccess
  ) {
    patchState({currentUser});
  }

  @Action(ResendVerificationEmail)
  resendVerificationEmail(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: username }: ResendVerificationEmail
  ) {
    const request = this.http.post(environment.api + 'user/resend', {username});

    return dispatch(
      createRequestAction({
        request,
        state: ResendVerificationEmailRequestState,
        successAction: ResendVerificationEmailSuccess,
        failAction: ResendVerificationEmailFail,
      })
    );
  }

  @Action(VerifyEmail)
  verifyEmail(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: {username, verification_code, registration_code}}: VerifyEmail
  ) {
    let params: any = { username };
    if (verification_code) {
      params = { ...params, verification_code };
    } else if (registration_code) {
      params = { ...params, registration_code };
    } else {
      throw new Error('Either verification_code or registration_code must be provided');
    }
    const request = this.http.get(environment.api + 'user/verify', { params });

    return dispatch(
      createRequestAction({
        request,
        state: VerifyEmailRequestState,
        successAction: VerifyEmailSuccess,
        failAction: VerifyEmailFail,
      })
    );
  }

  @Action(VerifyEmailSuccess)
  verifyEmailSuccess(
    { payload }: VerifyEmailSuccess
  ) {
  }

  @Action(ResetPassword)
  resetPassword(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: username }: ResetPassword
  ) {
    const request = this.http.post(environment.api + 'user/password/reset/send_email', {username});

    return dispatch(
      createRequestAction({
        request,
        state: ResetPasswordRequestState,
        successAction: ResetPasswordSuccess,
        failAction: ResetPasswordFail,
      }),
    );
  }

  @Action(ResetPasswordConfirm)
  resetPasswordConfirm(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: body }: ResetPasswordConfirm
  ) {
    const request = this.http.post(environment.api + 'user/password/reset', body);

    return dispatch(
      createRequestAction({
        request,
        state: ResetPasswordConfirmRequestState,
        successAction: ResetPasswordConfirmSuccess,
        failAction: ResetPasswordConfirmFail,
      }),
    );
  }

  @Action(ResetPasswordConfirmSuccess)
  resetPasswordConfirmSuccess(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: user }: SignInSuccess,
  ) {
    this.localStorageService.setToken(user.authorization);

    dispatch([new SetUser(user), new SetToken(user.authorization), new Navigate(['/', 'home'])]);
  }

  @Action(UpdatePassword)
  updatePassword(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: body }: UpdatePassword
  ) {
    const request = this.http.post(environment.api + 'user/password/update', body);

    return dispatch(
      createRequestAction({
        request,
        state: UpdatePasswordRequestState,
        successAction: UpdatePasswordSuccess,
        failAction: UpdatePasswordFail
      })
    );
  }

  @Action(UpdatePasswordSuccess)
  updatePasswordSuccess(
    { patchState }: StateContext<AuthStateModel>,
    { payload: currentUser }: UpdatePasswordSuccess
  ) {
    this.localStorageService.setToken(currentUser.authorization);
    patchState({currentUser, authorization: currentUser.authorization});
  }

  @Action(UpdateUser)
  updateUser(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: userData }: UpdateUser
  ) {
    const request = this.http.put(environment.api + 'user/update', userData);

    return dispatch(
      createRequestAction({
        request,
        state: UpdateUserRequestState,
        successAction: UpdateUserSuccess,
        failAction: UpdateUserFail,
        metadata: userData
      })
    );
  }

  @Action(UpdateUserSuccess)
  updateUserSuccess(
    { getState, patchState }: StateContext<AuthStateModel>,
    { payload: response, metadata: userData }: UpdateUserSuccess
  ) {
    const currentUser = {...getState().currentUser, ...userData};
    patchState({currentUser});
  }

  @Action(AcceptTerms)
  acceptTerms(
    { dispatch }: StateContext<AuthStateModel>,
    { payload: tokenAcceptance }: AcceptTerms
  ) {
    const request = this.http.post(environment.api + 'user/accept_terms_and_conditions', tokenAcceptance);

    return dispatch(
      createRequestAction({
        request,
        state: AcceptTermsRequestState,
        successAction: AcceptTermsSuccess,
        failAction: AcceptTermsFail,
        metadata: tokenAcceptance
      })
    );
  }
  
  @Action(AcceptTermsSuccess)
  acceptTermsSuccess(
    { getState, patchState }: StateContext<AuthStateModel>,
    { payload: response, metadata: tokenAcceptance }: AcceptTermsSuccess
  ) {
    console.log({response}, {tokenAcceptance});
  }

  @Action(LogOut)
  logOut(
    { dispatch }: StateContext<AuthStateModel>
  ) {
    const request = this.http.get(environment.api + 'user/logout');

    return dispatch(
      createRequestAction({
        request,
        state: LogOutRequestState,
        successAction: LogOutSuccess,
        failAction: LogOutFail,
      }),
    );
  }
}
